import {
  Button,
  ColumnTable,
  DetailBox,
  DetailBoxColumn,
  DisplayInfo,
  Icon,
  Loading,
  Modal,
  Table,
  addToast,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import { financialPortalPaymentHistoryDetail } from '../../../api/requests/financialPortal';
import { moneyFormatter } from '../../../utils/currency';
import { PaymentHistorySource } from '../PaymentHistory/types';

import {
  PaymentHistoryDetailDebt,
  PaymentHistoryDetailResponse,
} from './payment-history-detail.types';

const dictPrefix = 'financialPortal.paymentHistory.detailModal';

interface Props {
  /**
   * Identificador compuesto del listado, formato `{source}-{uuid}`. El parser
   * lo separa en `source` y `id` para llamar al endpoint.
   */
  paymentRowId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Si true, el header del modal incluye la fila Estudiante (XD a 2 – 24
   * cuando el modal se abre desde el listado general HU-15462). Para el
   * individual (HU-15469) se omite porque el contexto del alumno es
   * implícito desde la URL.
   */
  includeStudent?: boolean;
}

const formatDate = (value: string | null) =>
  value ? dayjs(value).utc().format('DD/MM/YYYY') : '-';

const formatAmount = (value: number | null | undefined) =>
  value != null ? moneyFormatter().format(Math.ceil(value)) : '-';

const fallback = (value: string | number | null | undefined) =>
  value == null || value === '' ? '-' : String(value);

const formatPerson = (
  name: string | null,
  identification: string | null,
): string => {
  if (!name && !identification) {
    return '-';
  }
  if (!identification) {
    return name ?? '-';
  }
  if (!name) {
    return identification;
  }
  return `${name} - ID ${identification}`;
};

const PAYMENT_HISTORY_SOURCES: PaymentHistorySource[] = ['debt-payment', 'refund'];

/**
 * El id compuesto es `{source}-{id}`, pero el source puede contener guiones
 * (`debt-payment`), así que NO se puede cortar en el primer `-`: hay que
 * matchear contra los prefijos conocidos.
 */
function parsePaymentRowId(
  rowId: string | null,
): { source: PaymentHistorySource; id: string } | null {
  if (!rowId) {
    return null;
  }
  for (const source of PAYMENT_HISTORY_SOURCES) {
    const prefix = `${source}-`;
    if (rowId.startsWith(prefix)) {
      const id = rowId.slice(prefix.length);
      return id ? { source, id } : null;
    }
  }
  return null;
}

export function PaymentHistoryDetailModal({
  paymentRowId,
  isOpen,
  onClose,
  includeStudent = false,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<PaymentHistoryDetailResponse | null>(null);

  const fetchDetail = useCallback(
    async (source: PaymentHistorySource, id: string) => {
      setLoading(true);
      setData(null);
      const response = await financialPortalPaymentHistoryDetail(source, id);
      if (response.error) {
        addToast({
          text: t(`${dictPrefix}.errorOnLoad`),
          color: 'danger',
          icon: 'error',
        });
        setLoading(false);
        return;
      }
      setData(response.data ?? null);
      setLoading(false);
    },
    [t],
  );

  useEffect(() => {
    const parsed = parsePaymentRowId(paymentRowId);
    if (isOpen && parsed) {
      fetchDetail(parsed.source, parsed.id);
    }
    if (!isOpen) {
      setData(null);
    }
  }, [isOpen, paymentRowId, fetchDetail]);

  const isRefund = data?.payment.source === 'refund';
  const isTransferRefund = isRefund && data?.payment.refundType === 'TRANSFER';
  const isDiscountRefund = isRefund && data?.payment.refundType === 'DISCOUNT';

  const headerColumns = useMemo<DetailBoxColumn[]>(() => {
    if (!data) {
      return [];
    }
    const cols: DetailBoxColumn[] = [];

    // ====== REFUND TRANSFER: Estudiante / Fecha pago / Tipo dev / Titular / Banco / Tipo cuenta / N° cuenta / Monto abono ======
    if (isTransferRefund) {
      cols.push({
        title: t(`${dictPrefix}.header.student`),
        body: formatPerson(
          data.payment.studentName,
          data.payment.studentIdentification,
        ),
        size: { xs: 12, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.paymentDate`),
        body: formatDate(data.payment.paymentDate),
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.refundType`),
        body: t(`${dictPrefix}.refundTypes.TRANSFER`),
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.holderName`),
        body: fallback(data.payment.bankTransfer?.holderName),
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.bankName`),
        body: fallback(data.payment.bankTransfer?.bankName),
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.accountType`),
        body: data.payment.bankTransfer?.accountType
          ? t(
              `${dictPrefix}.accountTypes.${data.payment.bankTransfer.accountType}`,
              { defaultValue: data.payment.bankTransfer.accountType },
            )
          : '-',
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.accountNumber`),
        body: fallback(data.payment.bankTransfer?.accountNumber),
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.transferAmount`),
        body: formatAmount(Math.abs(data.payment.amount)),
        size: { xs: 6, md: 3 },
      });
      return cols;
    }

    // ====== REFUND DISCOUNT: Estudiante / Fecha pago / Tipo dev / Monto pago ======
    if (isDiscountRefund) {
      cols.push({
        title: t(`${dictPrefix}.header.student`),
        body: formatPerson(
          data.payment.studentName,
          data.payment.studentIdentification,
        ),
        size: { xs: 12, md: 4 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.paymentDate`),
        body: formatDate(data.payment.paymentDate),
        size: { xs: 6, md: 2 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.refundType`),
        body: t(`${dictPrefix}.refundTypes.DISCOUNT`),
        size: { xs: 6, md: 3 },
      });
      cols.push({
        title: t(`${dictPrefix}.header.amount`),
        body: formatAmount(data.payment.amount),
        size: { xs: 6, md: 3 },
      });
      return cols;
    }

    // ====== COLLECTION (existing layout) ======
    if (includeStudent) {
      cols.push({
        title: t(`${dictPrefix}.header.student`),
        body: formatPerson(
          data.payment.studentName,
          data.payment.studentIdentification,
        ),
        size: { xs: 12, md: 4 },
      });
    }
    cols.push(
      {
        title: t(`${dictPrefix}.header.sustainer`),
        body: formatPerson(
          data.payment.sustainerName,
          data.payment.sustainerIdentification,
        ),
        size: { xs: 12, md: 4 },
      },
      {
        title: t(`${dictPrefix}.header.paymentDate`),
        body: formatDate(data.payment.paymentDate),
        size: { xs: 6, md: 2 },
      },
      {
        title: t(`${dictPrefix}.header.paymentGateway`),
        body: data.payment.paymentGateway
          ? t(`${dictPrefix}.gateways.${data.payment.paymentGateway}`, {
              defaultValue: data.payment.paymentGateway,
            })
          : '-',
        size: { xs: 6, md: 2 },
      },
      {
        title: t(`${dictPrefix}.header.authorizationCode`),
        body: fallback(data.payment.authorizationCode),
        size: { xs: 6, md: 2 },
      },
      {
        title: t(`${dictPrefix}.header.voucherNumber`),
        body: fallback(data.payment.voucherNumber),
        size: { xs: 6, md: 2 },
      },
      {
        title: t(`${dictPrefix}.header.amount`),
        body: formatAmount(data.payment.amount),
        size: { xs: 6, md: 2 },
      },
    );
    return cols;
  }, [data, includeStudent, isTransferRefund, isDiscountRefund, t]);

  const collectionDebtColumns: ColumnTable<PaymentHistoryDetailDebt>[] = useMemo(
    () => [
      {
        headerText: t(`${dictPrefix}.debtsTable.program`),
        columnName: 'program',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string),
      },
      {
        headerText: t(`${dictPrefix}.debtsTable.matriculationDate`),
        columnName: 'matriculationDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => formatDate(value as string | null),
      },
      {
        headerText: t(`${dictPrefix}.debtsTable.periodEntry`),
        columnName: 'periodEntry',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string | null),
      },
      {
        headerText: t(`${dictPrefix}.debtsTable.concept`),
        columnName: 'concept',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string),
      },
      {
        headerText: t(`${dictPrefix}.debtsTable.installment`),
        columnName: 'installment',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string),
      },
      // Los tres montos van explícitos: el de la deuda, el que aplicó ESTE
      // pago y el saldo que quedó. Con una sola columna "Monto" (que traía el
      // saldo) una deuda saldada se veía en $0 (Bug #16500).
      {
        headerText: t(`${dictPrefix}.debtsTable.debtAmount`),
        columnName: 'debtAmount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => formatAmount(value as number | null),
      },
      {
        headerText: t(`${dictPrefix}.debtsTable.paidAmount`),
        columnName: 'amount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => (
          <strong>{formatAmount(value as number)}</strong>
        ),
      },
      {
        headerText: t(`${dictPrefix}.debtsTable.balance`),
        columnName: 'balance',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => formatAmount(value as number | null),
      },
    ],
    [t],
  );

  const transferDebtColumns: ColumnTable<PaymentHistoryDetailDebt>[] = useMemo(
    () => [
      {
        headerText: t(`${dictPrefix}.refundTransferTable.concept`),
        columnName: 'concept',
        cellFormat: ({ value }) => fallback(value as string),
      },
      {
        headerText: t(`${dictPrefix}.refundTransferTable.debtId`),
        columnName: 'debtId',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string | null),
      },
      {
        headerText: t(`${dictPrefix}.refundTransferTable.amount`),
        columnName: 'amount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => (
          <strong>{formatAmount(value as number)}</strong>
        ),
      },
    ],
    [t],
  );

  const discountDebtColumns: ColumnTable<PaymentHistoryDetailDebt>[] = useMemo(
    () => [
      {
        headerText: t(`${dictPrefix}.refundDiscountTable.concept`),
        columnName: 'concept',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string),
      },
      {
        headerText: t(`${dictPrefix}.refundDiscountTable.program`),
        columnName: 'program',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string),
      },
      {
        headerText: t(`${dictPrefix}.refundDiscountTable.periodEntry`),
        columnName: 'periodEntry',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => fallback(value as string | null),
      },
      {
        headerText: t(`${dictPrefix}.refundDiscountTable.matriculationDate`),
        columnName: 'matriculationDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => formatDate(value as string | null),
      },
      {
        headerText: t(`${dictPrefix}.refundDiscountTable.amount`),
        columnName: 'amount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => (
          <strong>{formatAmount(value as number)}</strong>
        ),
      },
    ],
    [t],
  );

  const tableConfig = useMemo(() => {
    if (isTransferRefund) {
      return {
        columns: transferDebtColumns,
        sectionTitle: t(`${dictPrefix}.refundTransferTable.sectionTitle`),
        noResults: t(`${dictPrefix}.debtsTable.noResults`),
      };
    }
    if (isDiscountRefund) {
      return {
        columns: discountDebtColumns,
        sectionTitle: t(`${dictPrefix}.refundDiscountTable.sectionTitle`),
        noResults: t(`${dictPrefix}.refundDiscountTable.noResults`),
      };
    }
    return {
      columns: collectionDebtColumns,
      sectionTitle: t(`${dictPrefix}.debtsTable.sectionTitle`),
      noResults: t(`${dictPrefix}.debtsTable.noResults`),
    };
  }, [
    isTransferRefund,
    isDiscountRefund,
    transferDebtColumns,
    discountDebtColumns,
    collectionDebtColumns,
    t,
  ]);

  if (!isOpen) {
    return null;
  }

  const modalTitle = isRefund
    ? t(`${dictPrefix}.refundTitle`)
    : t(`${dictPrefix}.title`);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <Container>
        {loading && (
          <Row>
            <Col xs={12} className="d-flex justify-content-center">
              <Loading
                textBody={t(`${dictPrefix}.loading`)}
                className="full-width"
              />
            </Col>
          </Row>
        )}
        {!loading && data && (
          <>
            <Row>
              <Col xs={12} className="text-center mb-3">
                <Icon name="information" size="40px" color="primary" />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <h1 className="text-dark fs-22 text-center mb-4">
                  {modalTitle}
                </h1>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col xs={12}>
                <DetailBox columns={headerColumns} />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-uppercase text-primary fw-700 mb-0">
                    {tableConfig.sectionTitle}
                  </h5>
                  {isRefund && data.payment.attachmentUrl && (
                    <a
                      href={data.payment.attachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary fw-700 d-flex align-items-center"
                      style={{ textDecoration: 'underline' }}
                    >
                      {t(`${dictPrefix}.downloadAttachment`)}
                      <span className="ml-2">
                        <Icon name="download" size="16px" color="primary" />
                      </span>
                    </a>
                  )}
                </div>
                <Table
                  isLoadingResults={false}
                  data={data.debts}
                  columns={tableConfig.columns}
                  noResultsText={
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <DisplayInfo
                        iconName="information"
                        textBody={tableConfig.noResults}
                        maxWidth="500px"
                      />
                    </div>
                  }
                />
              </Col>
            </Row>
          </>
        )}
        <Row className="mt-4">
          <Col xs={12} className="d-flex justify-content-end">
            <Button
              type="button"
              size="md"
              text={t(`${dictPrefix}.close`)}
              onClick={onClose}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
}
